import { useState } from 'react';
import { type AddImageMenuItemProps } from '..';
import InvisibleInput from '@/components/InvisibleInput';
import { FormattedMessage } from 'react-intl';
import useFileSelectorControls from '../../utils/useFileSelectorControls';
import UploadMenuItem from '../../utils/upload/UploadMenuItem';
import UploadMenuItemContent from '../../utils/upload/UploadMenuItem/UploadMenuItemContent';
import getFilesToUpload, { FileRejectionState } from './getFilesToUpload';
import ImageUploadOptionIcon from './ImageUploadOptionIcon';
import FileRejectionErrorMessage from './FileRejectionErrorMessage';
import { blockPageClose, unblockPageClose } from '@/lib/pageCloseBlocking';

type ImageUploadOptionProps = AddImageMenuItemProps & {
  variant: 'add_image_generic' | 'select_image' | 'take_photo';
  onUploadImages: (imageFiles: File[]) => Promise<void>;
  onLoadStart: () => string;
  onLoadEnd: (loaderId: string) => void;
  onRegisterError: () => string;
  onRemoveError: (errorId: string) => void;
  onRegisterWarning: () => string;
  onRemoveWarning: (warningId: string) => void;
};

const ImageUploadOption = ({
  variant,
  onUploadImages,
  onLoadStart,
  onLoadEnd,
  onRegisterError,
  onRemoveError,
  onRegisterWarning,
  onRemoveWarning,
  ...props
}: ImageUploadOptionProps) => {
  const [inputRef, openFileSelector, resetFileSelectorState] =
    useFileSelectorControls();

  const [uploading, setUploading] = useState(false);
  const [fileRejectionState, setFileRejectionState] = useState(
    FileRejectionState.NONE_REJECTED,
  );

  const [registeredErrorId, setRegisteredErrorId] = useState<string | null>(
    null,
  );
  const [registeredWarningId, setRegisteredWarningId] = useState<string | null>(
    null,
  );

  const dismissFileRejectionError = (): void => {
    setFileRejectionState(FileRejectionState.NONE_REJECTED);

    if (registeredErrorId !== null) {
      onRemoveError(registeredErrorId);

      setRegisteredErrorId(null);
    }
  };

  const dismissFileRejectionWarning = (): void => {
    if (registeredWarningId !== null) {
      onRemoveWarning(registeredWarningId);

      setRegisteredWarningId(null);
    }
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    dismissFileRejectionError();
    dismissFileRejectionWarning();

    const files = e.target.files;

    if (!files || files.length === 0) {
      return;
    }

    setUploading(true);

    const loaderId = onLoadStart();

    const [filesToUpload, newFileRejectionState] = getFilesToUpload(files);

    setFileRejectionState(newFileRejectionState);

    if (
      newFileRejectionState === FileRejectionState.ONE_FILE_AND_REJECTED ||
      newFileRejectionState === FileRejectionState.MULTIPLE_FILES_ALL_REJECTED
    ) {
      setRegisteredErrorId(onRegisterError());
    }

    if (
      newFileRejectionState ===
        FileRejectionState.MULTIPLE_FILES_ONE_REJECTED ||
      newFileRejectionState === FileRejectionState.MULTIPLE_FILES_SOME_REJECTED
    ) {
      setRegisteredWarningId(onRegisterWarning());
    }

    if (filesToUpload.length > 0) {
      const pageCloseBlockerId = blockPageClose();

      await onUploadImages(filesToUpload);

      unblockPageClose(pageCloseBlockerId);
    }

    resetFileSelectorState();

    setUploading(false);
    onLoadEnd(loaderId);
  };

  const hasError =
    fileRejectionState === FileRejectionState.ONE_FILE_AND_REJECTED ||
    fileRejectionState === FileRejectionState.MULTIPLE_FILES_ALL_REJECTED;

  return (
    <>
      <InvisibleInput
        type="file"
        accept="image/svg+xml, image/jpg, image/jpeg, image/png"
        onChange={handleFileInputChange}
        ref={inputRef}
        capture={variant === 'take_photo' ? 'environment' : undefined}
        multiple
      />
      <UploadMenuItem
        onClick={uploading || hasError ? undefined : openFileSelector}
        disabled={uploading}
        disableRipple={hasError}
        {...props}
      >
        <ImageUploadOptionIcon
          variant={variant}
          uploading={uploading}
          fileRejectionState={fileRejectionState}
        />
        <UploadMenuItemContent>
          {hasError ? (
            <FileRejectionErrorMessage
              fileRejectionState={fileRejectionState}
              onDismiss={dismissFileRejectionError}
            />
          ) : uploading ? (
            <FormattedMessage id="portfolio.challenge.files.add.upload_image.uploading_state.label" />
          ) : (
            <FormattedMessage
              id={`portfolio.challenge.files.add.menu.items.upload_image.labels.${variant}`}
            />
          )}
        </UploadMenuItemContent>
      </UploadMenuItem>
    </>
  );
};

export default ImageUploadOption;
